import { RouteComponentProps } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import qs from 'query-string';
import { ISelectOptions } from 'src/components/ui/Select/createSelect.types';
import { AppDispatch } from 'src/store';
import { IQueryParams } from 'src/types';
import { convertFilterToArray } from 'src/utils/convertFilter';
import { removeEmpty } from 'src/utils/removeEmpty';
import { createAction } from '@reduxjs/toolkit';

import { IMerchantAccount } from '../connectedProviders/accountsPayment/types';
import { IMerchant } from '../merchants/types';

import {
  createPaymentFormDirectRequest,
  createPaymentFormRoutingRequest,
  fetchMerchantsRequest,
  fetchPaymentMerchantAccountByFormRequest,
  fetchPaymentMerchantAccountByIdRequest,
  fetchPaymentMerchantAccountRequest,
  fetchPaymentProvidersSearchRequest,
  fetchPaymentRouteRequest,
  fetchPaymentRoutingRequest,
  fetchSitesRequest,
  fetchSystemGroupByIdRequest,
  fetchSystemGroupsRequest,
  removeImagePaymentFormRoutingRequest,
  updateImagePaymentFormRoutingRequest,
  updatePaymentFormDirectRequest,
  updatePaymentFormRoutingRequest,
} from './requestHandlers';
import { ICreateForm, IPaymentForm, IPaymentRoutingData, IPaymentSystemGroupForm, ISite } from './types';

const defaultParams = {
  page: '0',
  size: '25',
  sort: 'id,asc',
};

export const loadPaymentRoutingStart = createAction('payment_routing/routing/load/start');

export const loadPaymentRoutingListSuccess = createAction<IPaymentRoutingData>('payment_routing/routing/load/success');

export const loadPaymentRouteStart = createAction('payment_routing/route/load/start');

export const loadPaymentRouteListSuccess = createAction<Partial<IPaymentForm>>('payment_routing/route/load/success');

export const resetPaymentRouteItem = createAction('payment_routing/route/reset');

export const loadPaymentRoutingRequest = (
  history: RouteComponentProps['history'],
  params: IQueryParams = {},
  isInitialLoading?: boolean,
  type?: string,
) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (isInitialLoading) {
      dispatch(loadPaymentRoutingStart());
    }
    const initialParams = qs.parse(history.location.search);
    const mergedParams = type === 'reset' ? defaultParams : removeEmpty({ archived: false, ...defaultParams, ...initialParams, ...params });

    mergedParams.systemGroupFormIds = convertFilterToArray(mergedParams.systemGroupFormIds);
    mergedParams.siteIds = convertFilterToArray(mergedParams.siteIds);
    mergedParams.currencies = convertFilterToArray(mergedParams.currencies);
    mergedParams.providerNames = convertFilterToArray(mergedParams.providerNames);
    mergedParams.merchantAccountIds = convertFilterToArray(mergedParams.merchantAccountIds);
    mergedParams.names = convertFilterToArray(mergedParams.names);
    mergedParams.tags = convertFilterToArray(mergedParams.tags);

    const data = await fetchPaymentRoutingRequest(mergedParams);
    if (data) {
      dispatch(loadPaymentRoutingListSuccess(data));
      history.replace({
        search: qs.stringify(mergedParams, { skipNull: true }),
      });
    }
  };
};

export const loadPaymentRouteRequest = (id: string, isInitialLoading?: boolean) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (isInitialLoading) {
      dispatch(loadPaymentRouteStart());
    }
    const data = await fetchPaymentRouteRequest(id);
    if (data) {
      dispatch(loadPaymentRouteListSuccess(data));
    }
  };
};

export const fetchSystemGroups = async (params: IQueryParams): Promise<IPaymentSystemGroupForm[]> => fetchSystemGroupsRequest(params);

export const fetchSystemGroupsOptions = async (params: IQueryParams, initialId?: string): Promise<IPaymentSystemGroupForm[]> => {
  const res = await fetchSystemGroupsRequest(params);
  if (initialId) {
    const initialValue = await fetchSystemGroupByIdRequest(initialId);
    res.push(initialValue);
  }

  return uniqBy(res, 'id');
};

export const fetchMerchantAccountOptions = async (id?: string, initialMerchantId?: string): Promise<IMerchantAccount[]> => {
  const res = await fetchPaymentMerchantAccountByFormRequest(id);
  if (initialMerchantId) {
    const initialValue = await fetchPaymentMerchantAccountByIdRequest(initialMerchantId);
    res.push(initialValue);
  }

  return uniqBy(res, 'id');
};

export const fetchPaymentProvidersSearch = async (): Promise<ISelectOptions | []> => fetchPaymentProvidersSearchRequest();
export const fetchMerchants = async (): Promise<IMerchant[]> => fetchMerchantsRequest();
export const fetchSites = async (): Promise<ISite[]> => fetchSitesRequest();
export const fetchPaymentMerchantAccount = async (type: string): Promise<IMerchantAccount[]> => fetchPaymentMerchantAccountRequest(type);

export const createPaymentFormDirect = async ({ res, logo }: ICreateForm): Promise<Partial<IPaymentForm>> => {
  const data = await createPaymentFormDirectRequest(res);

  return data;
};
export const updatePaymentFormDirect = async ({ res, logo }: ICreateForm): Promise<Partial<IPaymentForm>> => {
  if (logo) {
    await updateImagePaymentFormRoutingRequest(res?.id, logo);
  }
  return updatePaymentFormDirectRequest(res);
};
export const createPaymentFormRouting = ({ res }: ICreateForm): Promise<Partial<IPaymentForm>> => createPaymentFormRoutingRequest(res);
export const updatePaymentFormRouting = async ({ res, logo }: ICreateForm): Promise<Partial<IPaymentForm>> => {
  if (logo) {
    await updateImagePaymentFormRoutingRequest(res?.id, logo);
  }
  return updatePaymentFormRoutingRequest(res);
};
export const removeImagePaymentFormRouting = (id: string): Promise<void> => removeImagePaymentFormRoutingRequest(id);
